import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';

interface FadeInImageProps extends React.ComponentProps<'img'> {
  src?: string;
  alt?: string;
  className?: string;
  containerClassName?: string;
  fallbackContent?: React.ReactNode;
  noPlaceholderBg?: boolean;
  forceFadeIn?: boolean;
}

export const FadeInImage = React.memo(function FadeInImage({ 
  src, 
  alt, 
  className = '', 
  containerClassName = '',
  fallbackContent,
  noPlaceholderBg,
  forceFadeIn,
  ...props 
}: FadeInImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInstant, setIsInstant] = useState(false);
  
  // Track when the component mounts to measure how long the image takes to load
  const [mountTime] = useState(() => Date.now());

  // Use standard decoding and loading attributes by default for performance
  const defaultProps = {
    decoding: 'async' as const,
  };

  const isWFull = className.includes('w-full') || (containerClassName && containerClassName.includes('w-full'));
  const isHFull = className.includes('h-full') || (containerClassName && containerClassName.includes('h-full'));

  const hasExplicitContainerWidth = containerClassName && (containerClassName.includes('w-') || containerClassName.includes('flex-1'));
  const hasExplicitContainerHeight = containerClassName && (containerClassName.includes('h-') || containerClassName.includes('aspect-'));

  const animDuration = (!forceFadeIn && isInstant) ? 0 : 0.7;

  return (
    <div 
      className={`relative inline-flex items-center justify-center overflow-hidden ${containerClassName}`}
      style={{
        width: hasExplicitContainerWidth ? undefined : (isWFull ? '100%' : 'max-content'),
        height: hasExplicitContainerHeight ? undefined : (isHFull ? '100%' : 'max-content')
      }}
    >
      {/* Background/Fallback shown while loading */}
      <AnimatePresence>
        {(!isLoaded && !hasError && !noPlaceholderBg) && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ delay: 0.1, duration: 0.3 }} // Delay placeholder to prevent flash on fast loads
            className={`absolute inset-0 flex items-center justify-center overflow-hidden animate-pulse bg-white/10 ${className}`}
          >
            {fallbackContent}
          </motion.div>
        )}
      </AnimatePresence>

      {/* The Actual Image */}
      {src && !hasError ? (
        <motion.img
          src={src}
          alt={alt || ''}
          initial={{ opacity: 0 }}
          animate={{ opacity: isLoaded ? 1 : 0 }}
          transition={{ duration: animDuration, ease: "easeInOut" }}
          {...defaultProps}
          {...props}
          onLoad={(e) => {
            const loadTime = Date.now() - mountTime;
            // If it loaded in less than 50ms, it was probably in the browser cache!
            if (loadTime < 50) {
              setIsInstant(true);
            }
            setIsLoaded(true);
            if ((props as any).onLoad) (props as any).onLoad(e as any);
          }}
          onError={(e) => {
            setHasError(true);
            if ((props as any).onError) (props as any).onError(e as any);
          }}
          className={className}
        />
      ) : hasError ? (
        fallbackContent || <div className={`w-full h-full bg-black/20 ${className}`} />
      ) : null}
    </div>
  );
});
